import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { cancelPlan, getGuarantee, getStripeStatus, GUARANTEE_QUERY_KEY, STRIPE_STATUS_QUERY_KEY, type Cancellation } from '../api/endpoints/stripe';
import { MEMBERSHIP_QUERY_KEY } from '../api/endpoints/membership';
import { formatMoneyCents } from '../lib/format';
import { ConfirmAction } from './ConfirmAction';
import { ErrorBanner } from './ErrorBanner';

const LONG_DATE = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Panama' });

function longDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : LONG_DATE.format(date);
}

function outcomeLine(outcome: Cancellation): string {
  if (outcome.mode === 'refunded') {
    return `Listo. Te devolvimos ${formatMoneyCents(outcome.refundedCents)} a tu tarjeta y tu negocio pasó al plan Reservas. El banco lo muestra en 5 a 10 días hábiles.`;
  }
  if (outcome.mode === 'at_period_end' && outcome.endsAt) {
    return `Listo. No se te cobra más y conservas tu plan hasta el ${longDate(outcome.endsAt)}.`;
  }
  return 'Listo. Tu plan quedó cancelado.';
}

/**
 * "Cancelar tu plan" on Tu membresía: only for a plan paid by card through
 * Stripe. Says, before the click, exactly what happens: the 30-day guarantee
 * with the amount and its last day, or the date the plan keeps running to.
 */
export function CancelPlan() {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: STRIPE_STATUS_QUERY_KEY, queryFn: getStripeStatus });
  const subscribed = status.data?.configured === true && status.data.subscribed;
  const guarantee = useQuery({ queryKey: GUARANTEE_QUERY_KEY, queryFn: getGuarantee, enabled: subscribed });
  const mutation = useMutation({
    mutationFn: () => cancelPlan(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STRIPE_STATUS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GUARANTEE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MEMBERSHIP_QUERY_KEY });
    },
  });

  if (mutation.data) {
    return (
      <section className="section" aria-label="Cancelar tu plan">
        <p className="row-sub" role="status">
          {outcomeLine(mutation.data)}
        </p>
      </section>
    );
  }
  if (!subscribed || !guarantee.data) return null;
  const { eligible, until, refundCents, cancelAt } = guarantee.data;

  return (
    <section className="section" aria-label="Cancelar tu plan">
      <h2 className="section-label">Cancelar tu plan</h2>
      {cancelAt ? (
        <p className="row-sub">Tu plan termina el {longDate(cancelAt)}. No se te cobrará más.</p>
      ) : (
        <>
          {eligible && until ? (
            <p className="row-sub">
              Estás dentro de la garantía de 30 días, hasta el {longDate(until)}. Si cancelas hoy, te devolvemos {formatMoneyCents(refundCents)} completos a tu
              tarjeta y tu negocio pasa al plan Reservas, sin preguntas.
            </p>
          ) : (
            <p className="row-sub">Si cancelas, no se te cobra más y conservas tu plan hasta el final del periodo que ya pagaste.</p>
          )}
          <ConfirmAction
            label="Cancelar plan"
            confirmLabel="Sí, cancelar"
            cancelLabel="No, seguir"
            pendingLabel="Cancelando…"
            variant="danger"
            pending={mutation.isPending}
            onConfirm={() => mutation.mutate()}
          />
        </>
      )}
      {mutation.isError ? (
        <ErrorBanner message={mutation.error instanceof ApiError ? mutation.error.message : 'No pudimos cancelar ahora. Intenta de nuevo.'} />
      ) : null}
    </section>
  );
}
